import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, Check, X, ShieldAlert, Trash2 } from "lucide-react";

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, withCredentials: true });
const TAB_LABELS = {
  overview: "Overview", enquiries: "Enquiries", inquiries: "Inquiries", quotes: "Quotes", "service-requests": "Service", applications: "Applications",
  "career-apps": "Career Apps", people: "HR & Payroll", team: "Team", letters: "Appointment Letters", idcards: "ID Cards", payments: "Payments",
  products: "Store", signature: "Signature", "email-setup": "Email Setup", holidays: "Holidays", leave: "Leave",
};
const fmt = (d) => (d ? new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
const STATUS_CLS = { pending: "text-amber-300 border-amber-500/40", applied: "text-emerald-300 border-emerald-500/40", scheduled: "text-sky-300 border-sky-500/40",
  rejected: "text-red-300 border-red-500/40", failed: "text-red-300 border-red-500/40", no_profile: "text-slate-400 border-white/20" };

const Chip = ({ on, onClick, label, testId }) => (
  <button type="button" onClick={onClick} className={`text-[11px] px-2.5 py-1 border transition-colors ${on ? "border-nx-gold/60 bg-nx-gold/10 text-white" : "border-white/10 text-slate-500 hover:text-slate-300"}`} data-testid={testId}>{label}</button>
);

const ProfileEditor = ({ designation, cat, initial, onSaved }) => {
  const [role, setRole] = useState(initial?.tt_role || "support");
  const [portal, setPortal] = useState(initial?.portal_modules ?? ["home", "org"]);
  const [hidden, setHidden] = useState(initial?.hidden_sections || []);
  const [erp, setErp] = useState(initial?.erp_roles?.length ? initial.erp_roles : (cat.erp_role_defaults[initial?.tt_role || "support"] || []));
  const [saving, setSaving] = useState(false);
  const changeRole = (r) => { setRole(r); setErp(cat.erp_role_defaults[r] || []); setHidden([]); };
  const tabs = cat.role_sections[role] || [];
  const toggle = (list, set, k) => set(list.includes(k) ? list.filter((x) => x !== k) : [...list, k]);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/access-profiles/${encodeURIComponent(designation)}`, { tt_role: role, portal_modules: portal, hidden_sections: hidden, erp_roles: erp });
      toast.success(`Access profile saved for ${designation}`);
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save profile"); } finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove the access profile for ${designation}? New employees with this designation will get no automatic access.`)) return;
    try { await api.delete(`/access-profiles/${encodeURIComponent(designation)}`); toast.success("Profile removed"); onSaved(); } catch (err) { toast.error(err?.response?.data?.detail || "Could not remove"); }
  };
  return (
    <div className="border border-nx-gold/30 bg-nx-gold/[0.03] p-4 space-y-4" data-testid={`profile-editor-${designation}`}>
      <div>
        <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-2">Team Tracker role</p>
        <div className="flex flex-wrap gap-1.5">
          {cat.staff_roles.map((r) => <Chip key={r} on={role === r} onClick={() => changeRole(r)} label={r + (cat.privileged_roles.includes(r) ? " · approval" : "")} testId={`profile-role-${r}`} />)}
        </div>
        {cat.privileged_roles.includes(role) && <p className="text-[11px] text-amber-300 mt-2 flex items-center gap-1.5"><ShieldAlert size={12} /> Privileged — every employee saved with this designation needs your Telegram approval before access is granted.</p>}
      </div>
      <div>
        <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-2">Team Tracker tabs (from role)</p>
        {tabs.length === 0 ? <p className="text-xs text-slate-500">{role === "admin" ? "Admin sees everything." : "This role has no Team Tracker tabs."}</p> : (
          <div className="flex flex-wrap gap-1.5">{tabs.map((k) => <Chip key={k} on={!hidden.includes(k)} onClick={() => toggle(hidden, setHidden, k)} label={TAB_LABELS[k] || k} testId={`profile-tab-${k}`} />)}</div>
        )}
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500">People portal modules</p>
          <div className="flex gap-3 text-[10px] uppercase tracking-wide"><button onClick={() => setPortal(cat.portal_modules.map((m) => m.key))} className="text-nx-goldlight hover:underline">All</button><button onClick={() => setPortal(["home", "org"])} className="text-slate-400 hover:underline">Basic</button></div>
        </div>
        <div className="flex flex-wrap gap-1.5">{cat.portal_modules.map((m) => <Chip key={m.key} on={portal.includes(m.key)} onClick={() => toggle(portal, setPortal, m.key)} label={m.label} testId={`profile-portal-${m.key}`} />)}</div>
      </div>
      <div>
        <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-2">ERPNext roles</p>
        <div className="flex flex-wrap gap-1.5">{cat.erp_roles.map((r) => <Chip key={r} on={erp.includes(r)} onClick={() => toggle(erp, setErp, r)} label={r} testId={`profile-erp-${r.replace(/\s/g, "-")}`} />)}</div>
      </div>
      <div className="flex items-center justify-between gap-3 pt-1">
        {initial ? <button onClick={remove} className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-red-300 hover:text-red-200" data-testid="profile-remove"><Trash2 size={12} /> Remove profile</button> : <span />}
        <button onClick={save} disabled={saving} className="bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-5 py-2.5 disabled:opacity-60" data-testid="profile-save">{saving ? "Saving…" : "Save profile"}</button>
      </div>
    </div>
  );
};

export const AccessProfilesModal = ({ onClose }) => {
  const [cat, setCat] = useState(null);
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(null);
  const [q, setQ] = useState("");
  const load = async () => {
    try {
      const [{ data }, { data: r }] = await Promise.all([api.get("/access-profiles"), api.get("/access-profiles/requests")]);
      setCat(data); setRequests(r);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not load access profiles"); onClose(); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const decide = async (id, action) => {
    try { await api.post(`/access-profiles/requests/${id}/decide`, { action }); toast.success(action === "approve" ? "Approved — access is being set up" : "Rejected"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not decide"); }
  };
  const list = useMemo(() => (cat?.designations || []).filter((d) => d.toLowerCase().includes(q.toLowerCase())), [cat, q]);
  const pending = requests.filter((r) => r.status === "pending");
  return (
    <div data-lenis-prevent className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="access-profiles-modal">
      <div className="bg-nx-navy border border-white/15 w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-white/10">
          <div>
            <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500 flex items-center gap-2"><KeyRound size={13} className="text-nx-goldlight" /> Access profiles by designation</p>
            <p className="text-xs text-slate-400 mt-1">Set once per designation. When HR saves an Employee in ERPNext with a company email, the login, Team Tracker role, portal modules and ERPNext roles are granted automatically.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-xl leading-none px-1" data-testid="access-profiles-close">×</button>
        </div>
        {!cat ? <p className="px-5 py-8 text-sm text-slate-500">Loading…</p> : (
          <div className="overflow-y-auto px-5 py-4 space-y-5">
            {pending.length > 0 && (
              <div className="border border-amber-500/40 bg-amber-500/[0.06] p-4" data-testid="access-pending-panel">
                <p className="text-xs font-semibold text-amber-200 mb-2 flex items-center gap-2"><ShieldAlert size={13} /> {pending.length} privileged access request{pending.length > 1 ? "s" : ""} awaiting your approval</p>
                <div className="space-y-2">
                  {pending.map((r) => (
                    <div key={r.id} className="flex flex-wrap items-center gap-2 text-xs border-b border-white/5 pb-2" data-testid={`access-req-${r.id}`}>
                      <span className="text-white font-medium">{r.employee_name}</span><span className="text-slate-500">{r.employee} · {r.designation} · {r.company_email}</span>
                      <span className="text-slate-400">→ role {r.profile?.tt_role}</span>
                      <span className="ml-auto flex gap-1.5">
                        <button onClick={() => decide(r.id, "approve")} className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-500 text-white px-2.5 py-1 text-[11px] font-semibold uppercase" data-testid={`access-approve-${r.id}`}><Check size={11} /> Approve</button>
                        <button onClick={() => decide(r.id, "reject")} className="flex items-center gap-1 border border-red-500/50 text-red-300 hover:bg-red-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase" data-testid={`access-reject-${r.id}`}><X size={11} /> Reject</button>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search designation…" className="w-full bg-white/[0.04] border border-white/10 focus:border-nx-gold/50 text-sm text-white px-3 py-2 outline-none" data-testid="access-profiles-search" />
            <div className="space-y-1.5" data-testid="access-profiles-list">
              {list.map((d) => {
                const p = cat.profiles[d];
                return (
                  <div key={d}>
                    <button onClick={() => setOpen(open === d ? null : d)} className={`w-full flex flex-wrap items-center gap-2 text-left border px-3 py-2 transition-colors ${p ? "border-emerald-500/30 bg-emerald-500/[0.04]" : "border-white/10 hover:border-white/25"}`} data-testid={`access-profile-row-${d}`}>
                      <span className="text-sm text-white">{d}</span>
                      {p ? <span className="text-[10px] text-emerald-300 uppercase tracking-wide">role {p.tt_role} · {p.erp_roles?.length || 0} ERP role{p.erp_roles?.length === 1 ? "" : "s"} · {p.portal_modules?.length || 0} modules</span>
                         : <span className="text-[10px] text-slate-500 uppercase tracking-wide">no profile — set one</span>}
                      <span className="ml-auto text-slate-500 text-xs">{open === d ? "▲" : "▼"}</span>
                    </button>
                    {open === d && <ProfileEditor designation={d} cat={cat} initial={p} onSaved={() => { setOpen(null); load(); }} />}
                  </div>
                );
              })}
              {list.length === 0 && <p className="text-xs text-slate-500">No designations match.</p>}
            </div>
            {requests.length > 0 && (
              <div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-2">Recent access activity</p>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {requests.slice(0, 30).map((r) => (
                    <div key={r.id} className="flex flex-wrap items-center gap-2 text-[11px] border-b border-white/5 pb-1" data-testid={`access-log-${r.id}`}>
                      <span className={`px-1.5 py-0.5 border uppercase tracking-wide ${STATUS_CLS[r.status] || STATUS_CLS.no_profile}`}>{r.status.replace("_", " ")}</span>
                      <span className="text-slate-200">{r.employee_name}</span><span className="text-slate-500">{r.designation} · {r.company_email}</span>
                      <span className="ml-auto text-slate-600">{fmt(r.applied_at || r.decided_at || r.created_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
